import React from 'react';
import GuideHero from 'components/guide-hero';
import {
  CitationBlock,
  CitationBlockMini,
  CitationExampleBox,
  GuideHeader,
  GuideSection,
  GuideTitle,
  GuideWrapper,
  IntextCitation,
  TextWithColor,
} from 'components/guide';
import GuideLinkButton, {
  GuideLinkButtonSection,
} from 'components/guide-link-button';
import Footer from 'components/footer';
import Link from 'next/link';

const IEEEGuide: React.FC = () => (
  <>
    <GuideHero
      header="Our Complete Guide to Citing in IEEE Format"
      imgSrc={require('images/IEEE-guide-hero.png')}
      imgAlt="Laptop with screenshot on screen of manual for IEEE format"
    >
      <p>
        In this comprehensive guide, we will explore how to write an academic
        paper in IEEE, including formatting in-text citations and reference
        lists.
      </p>
      <p>
        Imagine each citation as a vibrant beam of light, illuminating the path
        from an idea&apos;s inception to its eventual metamorphosis into shared
        knowledge. These beams, arranged meticulously in the constellation of
        the IEEE citation format, guide us through the cosmos of innovation.
      </p>
      <p>
        As we delve deeper into this guide, prepare yourself for an exhilarating
        journey to decode and master the IEEE citation format!
      </p>
    </GuideHero>
    <GuideWrapper>
      <GuideTitle>Discover More Bibcitation Guides</GuideTitle>
      <GuideLinkButtonSection>
        <GuideLinkButton
          text="Chicago Guide"
          href="/chicago"
          variant="purple"
        />
        <GuideLinkButton text="APA Guide" href="/apa" variant="blue" />
        <GuideLinkButton text="MLA Guide" href="/mla" variant="orange" />
      </GuideLinkButtonSection>
      <Overview />
      <IntextCitations />
      <ReferenceList />
      <CiteBooks />
      <CiteJournalArticles />
      <CiteWebsites />
      <CiteMagazinesNewspapers />
      <CiteConferencePapers />

      <CiteSocialMedia />
      <CiteImagesArtworks />
      <CiteThesesDissertations />
      <CiteDataSetsDatabases />
      <CiteMobileApplications />
      <CiteTechnicalReports />
      <CitePatents />
      <CiteCourseMaterials />
      <CitePersonalCommunications />
      <FinalNotes />
    </GuideWrapper>
    <Footer />
  </>
);

const Overview: React.FC = () => (
  <GuideSection id="Overview">
    <GuideHeader>IEEE Format Overview</GuideHeader>
    <p>
      The IEEE (Institute of Electrical &amp; Electronics Engineers) citation
      format is commonly used in technical fields such as computer science,
      engineering, and information technology. This format guides writers on how
      to cite references and construct bibliographies for research papers,
      theses, and dissertations. The IEEE style has several specific guidelines
      regarding formatting in-text citations and reference lists:
    </p>

    <p>
      <strong>In-text citations:</strong> The IEEE citation style uses numbers
      enclosed in square brackets for in-text citations, which are enumerated in
      the order they appear in the text. When you cite the same source multiple
      times, you use the same number assigned to it.
    </p>

    <p>
      <strong>Reference list:</strong> At the end of your document, you provide
      a list of references, each tagged with its corresponding number as used in
      the text. This list includes the full details of the sources, starting
      with the assigned number, and is typically arranged in the order of their
      appearance in the text.
    </p>

    <p>
      The IEEE citation format aims to standardize the presentation of
      references in technical writing, making it easier for readers to locate
      the original sources, while ensuring due credit is given to the authors of
      the sources.
    </p>
  </GuideSection>
);

const IntextCitations: React.FC = () => (
  <GuideSection id="Intext">
    <GuideHeader>Insert IEEE In-text Citations</GuideHeader>
    <p>
      In the following guide, we will unravel the specifics of IEEE in-text
      citation formatting, taking you through a detailed journey from sequencing
      your citations to using them in direct quotes. Let&apos;s demystify the
      process and learn how to give credit where credit is due, IEEE style!
    </p>

    <p>
      Here is a step-by-step guide to formatting in-text citations in the IEEE
      citation style:
    </p>
    <ul>
      <li>
        <strong>Sequence:</strong> In IEEE style, in-text references should be
        numbered in the order they appear in the text. So, the first source you
        cite should be numbered <IntextCitation>[1]</IntextCitation>, the second
        source <IntextCitation>[2]</IntextCitation>, and so on.
      </li>
      <li>
        <strong>Placement:</strong> The citation number is enclosed in square
        brackets and should be placed before the punctuation if it&apos;s at the
        end of a sentence. For example, &quot;This was later affirmed by{' '}
        <IntextCitation>Smith [2]</IntextCitation>.&quot;
      </li>
      <li>
        <strong>Repeated citations:</strong> When you cite the same source again
        later in your text, you use the same number that was assigned to it when
        it was first cited. You do not need to provide the full citation again.
      </li>
      <li>
        <strong>Multiple citations:</strong> If you&apos;re citing multiple
        sources at once, each number should be enclosed in separate brackets
        with commas separating each one. For example, &quot;
        <IntextCitation>[1], [3], [5]</IntextCitation>&quot;. If you&apos;re
        citing many consecutive sources, you can use a hyphen to connect the
        first and last numbers, like
        <IntextCitation>[1]-[3]</IntextCitation>, to include references 1, 2,
        and 3.
      </li>
      <li>
        <strong>Direct quotes:</strong> If you&apos;re citing a direct quote,
        the citation number should be placed directly after the quote, and you
        should also provide the page number in the reference list.
      </li>
      <li>
        <strong>Citing specific parts of a source:</strong> If you are citing a
        specific part or parts of a source (for example, a figure or a specific
        page), you should give the relevant page number or numbers in the
        citation.
      </li>
      <li>
        <strong>Author&apos;s name in text:</strong> If you mention the
        author&apos;s name in the text, you still need to include the citation
        number. For example, &quot;According to Smith{' '}
        <IntextCitation>[2]</IntextCitation>, the experiment...&quot;
      </li>
    </ul>

    <p>
      Remember, in-text citations are meant to guide the reader to the full
      citation in your reference list, and they should be used whenever another
      person&apos;s work has influenced or is directly quoted in your paper.
    </p>
  </GuideSection>
);

const ReferenceList: React.FC = () => (
  <GuideSection>
    <GuideHeader>Format Your IEEE Reference List</GuideHeader>
    <p>
      In this comprehensive guide, we&apos;ll examine the main elements of the
      IEEE reference list style, from numbering and ordering to detailing
      various source types.
    </p>
    <ul>
      <li>
        <strong>Reference list title:</strong> The list of references should
        appear at the end of your document. It should be headed by the title
        &quot;References&quot; (not &quot;Bibliography&quot;).
      </li>
      <li>
        <strong>Ordering:</strong> References should be listed in the order they
        are cited in the text, not in alphabetical order. This means the first
        source you cited in your paper should be the first reference in your
        list, and so on.
      </li>
      <li>
        <strong>Numbering:</strong> Each reference should begin with the
        corresponding citation number used in the text, enclosed in square
        brackets.
      </li>
      <li>
        <strong>Author names:</strong> Authors&apos; names should be listed as
        initial(s) and last name. For example: [1] A. Einstein and N. Bohr,....
        If a reference has more than three authors, list the first author&apos;s
        name followed by et al. For example: [2] B. Gates et al.,....
      </li>
      <li>
        <strong>Titles:</strong> The titles of articles, papers, and book
        chapters should be in quotation marks, with only the first word
        capitalized (as well as any proper nouns). The names of books, journals,
        magazines, and conferences should be in italics, with all main words
        capitalized.
      </li>

      <li>
        <strong>Indentation:</strong> Unlike other reference styles, IEEE does
        not require you to use a hanging indent for each reference.
      </li>
    </ul>

    <p>
      This guide should help you craft a comprehensive and accurate reference
      list, ensuring you give proper credit to the sources you&apos;ve used in
      your work.
    </p>
    <img
      src={require('images/IEEE-screenshot.png')}
      alt="Screenshot of an IEEE reference page"
    />
    <p>
      From assigning numerical citations in the order they appear in your
      document, to correctly listing authors, titles, and publication details
      for various types of sources, each step is integral in building an
      accurate IEEE reference list. Whether you are referencing a book, journal
      article, film, song, or video game, the IEEE style has a set structure to
      guide you.
    </p>
    <p>
      While it may seem complex at first, with consistent practice, citing in
      the IEEE format can become second nature. Remember, the goal is to give
      due credit to your sources and to help readers trace the lineage of ideas.
    </p>
  </GuideSection>
);

const CiteBooks: React.FC = () => (
  <GuideSection id="Book">
    <GuideHeader>Cite Books in IEEE</GuideHeader>
    <p>
      When citing a book in the IEEE citation style, you will typically include
      the author&apos;s name, the book title, the place of publication, the
      publisher, and the year of publication. When citing a book in the IEEE
      citation style, the general structure is:
    </p>
    <CitationBlock header="Citing a book in IEEE">
      [citation number] Author(s) Initial(s). Last Name, <em>Title of Book,</em>{' '}
      Place of Publication: Publisher, Year.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      [1] J. D. Salinger, <em>The Catcher in the Rye</em>, New York, NY: Little,
      Brown and Company, 1951.
    </CitationExampleBox>
    <p>Let&apos;s break down each component of this structure:</p>
    <ul>
      <li>
        <strong>Citation number:</strong> This is the number that corresponds to
        the in-text citation you use in your paper. It should be enclosed in
        square brackets.
      </li>
      <li>
        <strong>Author&apos;s initial(s) and last name:</strong> Write the
        author&apos;s first and middle initials (if applicable), followed by
        their last name. If the book has more than one author, list them in the
        order they appear on the title page, and use the word &quot;and&quot;
        before the last author&apos;s name.
      </li>
      <li>
        <strong>Title of book:</strong> The title of the book should be written
        in title case and italicized.
      </li>
      <li>
        <strong>Place of publication:</strong> Include the city and, if
        it&apos;s in the U.S., the state of the publisher. If the city is
        outside the U.S., include the country.
      </li>
      <li>
        <strong>Publisher:</strong> The publisher&apos;s name should be written
        as it appears on the title page of the book.
      </li>
      <li>
        <strong>Year:</strong> This is the year the edition you used was
        published.
      </li>
    </ul>
    <br />
    <p>
      However, there are some exceptions and special circumstances that may
      arise when citing a book in IEEE format:
    </p>
    <p>
      <strong>Citation for a book with multiple authors</strong>: When a book
      has two or three authors, list all the authors as they appear on the title
      page, separated by the word &quot;and&quot;. If a book has four or more
      authors, list the first author&apos;s name followed by &quot;et al.&quot;
    </p>

    <CitationExampleBox>
      [1] R. L. Graham, D. E. Knuth,{' '}
      <TextWithColor>and O. Patashnik</TextWithColor>,{' '}
      <em>Concrete Mathematics, </em>
      Reading, MA: Addison-Wesley, 1988.
    </CitationExampleBox>

    <p>
      <strong>Citation for a book with an editor instead of author</strong>: If
      the book has an editor instead of an author, include the editor&apos;s
      name followed by &quot;, Ed.&quot;
    </p>

    <CitationExampleBox>
      [2] <TextWithColor>J. D. McDowell, Ed.</TextWithColor>,{' '}
      <em>Handbook of Randomness,</em> New York, NY: Mc-Graw Hill, 1997.
    </CitationExampleBox>

    <p>
      <strong>Citation for a book with no author</strong>: If no author or
      editor is listed, begin the citation with the book title.
    </p>

    <CitationExampleBox>
      [3]{' '}
      <TextWithColor>
        <em>Oxford English Dictionary.</em>
      </TextWithColor>{' '}
      Oxford, U.K.: Oxford University Press, 2015.
    </CitationExampleBox>

    <p>
      <strong>Citation for a book with multiple editions:</strong> If the book
      is a second edition or beyond, include the edition after the title.
    </p>

    <CitationExampleBox>
      [4] S. Hawking, A Brief History of Time,{' '}
      <TextWithColor>10th ed.</TextWithColor> London, U.K.: Bantam, 1998.
    </CitationExampleBox>
    <p>
      <strong>Citation for a book chapter:</strong> If you&apos;re citing a
      specific book chapter, include the chapter title, the word &quot;in&quot;
      before the book title, and page numbers of the chapter after the book
      title.
    </p>

    <CitationExampleBox>
      [5] R. Feynman, &quot;Quantum behavior,&quot;{' '}
      <TextWithColor>
        in <em>The Feynman Lectures on Physics</em>
      </TextWithColor>
      , R. P. Feynman, R. B. Leighton, and M. Sands, Eds., Reading, MA:
      Addison-Wesley, 1963, <TextWithColor>pp. 37-52</TextWithColor>.
    </CitationExampleBox>
    <p>
      <strong>Citation for a book in a series</strong>: If the book is a
      multi-volume set or a part of a series, include the series title after the
      book title.
    </p>

    <CitationExampleBox>
      [6] W. H. Press, B. P. Flannery, and W. T. Vetter,
      <em> Numerical Recipes: The Art of Computing,</em>{' '}
      <TextWithColor>3rd ed., vol. 4., </TextWithColor>
      Cambridge, U.K.: Cambridge University Press, 2007.
    </CitationExampleBox>
    <p>
      These exceptions cover most special cases you might encounter when citing
      books in the IEEE style. Remember, the goal is to provide enough
      information for readers to find the source you&apos;re referencing.
    </p>
  </GuideSection>
);

const CiteJournalArticles: React.FC = () => (
  <GuideSection id="JournalArticles">
    <GuideHeader>Cite Journal Articles in IEEE</GuideHeader>
    <p>
      When writing an academic paper, citing journal articles appropriately in
      the IEEE style is pivotal, for it upholds academic integrity and
      acknowledges the contributions of fellow researchers. Let&apos;s learn how
      to navigate this maze of journal citation together.
    </p>
    <p>
      When citing a journal article in the IEEE citation style, the general
      structure is:
    </p>
    <CitationBlock header="Citing a journal article in IEEE">
      [citation number] Author(s) Initial(s). Last Name, &quot;Title of
      article,&quot; <em>Title of Journal,</em> vol. Volume, no. Issue, pp. page
      range, Year, DOI or URL (if available).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      [2] S. Zhang, &quot;Cloud computing data protection in the EU: The data
      protection directive versus the general data protection regulation,&quot;
      <em>Computer Law and Security Review</em>, vol. 34, no. 3, pp. 595-612,
      2018, doi: 10.0375/kgh9835t.
    </CitationExampleBox>
    <p>
      When citing a journal article in the IEEE citation style, you may also
      encounter a number of exceptions, such as articles only found in online
      databases. Below we have covered some of the most popular cases:
    </p>
    <p>
      <strong>Citation for a journal article with multiple authors:</strong>{' '}
      When a book has two or three authors, list all the authors in the order
      they appear on the title page, separated by the word &quot;and&quot;. If a
      book has four or more authors, list the first author&apos;s name followed
      by &quot;et al.&quot;
    </p>

    <CitationExampleBox>
      [1] <TextWithColor>R. J. Smith et al.</TextWithColor>, &quot;Effective
      machine learning approaches for navigation in the Arctic,&quot;{' '}
      <em>IEEE Journal on Cybernetics</em>, vol. 48, no. 3, pp. 841-853, 2018.
    </CitationExampleBox>
    <p>
      <strong>Citation for online journals:</strong> For online journals,
      include the DOI (Digital Object Identifier) or URL at the end of the
      citation.
    </p>
    <CitationExampleBox>
      [2] A. B. Smith, &quot;Quantum mechanics in computer science,&quot;
      <em> Journal of Quantum Computing,</em> vol. 2, no. 4, pp. 50-60, 2019,{' '}
      <TextWithColor>doi:10.1109/JQC.5.877098</TextWithColor>.
    </CitationExampleBox>

    <p>
      Do not forget that the primary purpose of citations is to equip readers
      with adequate information to track down the sources that have informed
      your work.
    </p>
  </GuideSection>
);

const CiteWebsites: React.FC = () => (
  <GuideSection id="Websites">
    <GuideHeader>Cite Websites in IEEE</GuideHeader>
    <p>
      Websites are an indispensable resource for research in the digital age,
      making it all the more important to credit them correctly in our work.
      Navigating the process can be daunting, but fear not — the IEEE style
      provides a straightforward way to cite online sources.
    </p>
    <p>
      When citing a website in the IEEE citation style, the general structure
      is:
    </p>
    <CitationBlock header="Citing a website in IEEE">
      [citation number] Author(s) Initial(s). Last Name, &quot;Title of
      Webpage.&quot; Website Name. Date. URL (Accessed Date).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      [1] J. D. Smith, &quot;Understanding Quantum Computing.&quot; Quantum
      Computing Today.
      https://www.quantumtoday.com/understanding-quantum-computing (accessed
      Sep. 14, 2010).
    </CitationExampleBox>
    <p>
      Now, let&apos;s cover some of the most popular exceptions when it comes to
      generating citations for websites in the IEEE style:
    </p>
    <p>
      <strong>Citation for a website with no author:</strong> If no author is
      listed, begin the citation with the title of the webpage.
    </p>

    <CitationExampleBox>
      [1]{' '}
      <TextWithColor>
        &quot;The Intriguing Lore of the Star Wars Universe.&quot;
      </TextWithColor>{' '}
      Galactic Encyclopedia. https://www.galacticencyclopedia.com/star-wars
      (accessed May 13, 2012).
    </CitationExampleBox>
    <p>
      <strong>Citation for a website with no title:</strong> In cases where no
      page title is available, you can include a page description in brackets.
    </p>

    <CitationExampleBox>
      [2] J. K. Rowling,{' '}
      <TextWithColor>[A letter to the community]</TextWithColor>. Wizarding
      World. https://wizardingworld.com/stories/untold-hogwarts (accessed Jan.
      20, 2025).
    </CitationExampleBox>

    <p>
      <strong>Citation for a blog post or article:</strong> If you are citing a
      blog post or an article on a website, the title of the post or article is
      listed first, followed by the title of the blog or website.
    </p>

    <CitationExampleBox>
      [3] S. T. Critic,{' '}
      <TextWithColor>
        &quot;The Influence of Anime on Global Pop Culture.&quot; Otaku
        Insights.{' '}
      </TextWithColor>
      https://otakuinsights.com/blog/anime-influence (accessed Aug. 6, 2015).
    </CitationExampleBox>
    <p>
      These are the most common exceptions you might encounter when citing a
      website in the IEEE style. Remember that the main purpose of a citation is
      to allow readers to find your source, so always provide as much
      information as you can find to help readers locate the original material.
    </p>
  </GuideSection>
);

const CiteMagazinesNewspapers: React.FC = () => (
  <GuideSection id="MagazinesNewspapers">
    <GuideHeader>Cite Magazines & Newspaper Articles in IEEE</GuideHeader>
    <p>
      Magazines and newspapers often explore the pulse of current events,
      trending discussions, and the cutting edge of knowledge across various
      fields. They remain a crucial source of information, despite the digital
      information overload. This is why when you cite a magazine or newspaper
      article in your work, you must acknowledge the original authors and
      provide a roadmap for your readers to follow.
    </p>
    <p>
      The typical format for citing a magazine or newspaper article in IEEE is:
    </p>
    <CitationBlock header="Citing a magazine or newspaper article in IEEE">
      [citation number] Author(s) Initial(s). Last Name, &quot;Title of
      Article,&quot; <em>Title of Periodical</em>, pp. page numbers (if
      available), Month Date, Year. URL (accessed date) if available.
    </CitationBlock>
    <p>
      <strong>Citing a magazine article:</strong>
    </p>
    <CitationExampleBox>
      [1] R. E. Viewer, &quot;The Evolution of Superhero Movies,&quot;{' '}
      <em>Pop Culture Analysis</em>, Jun. 2023.
      https://popcultureanalysis.com/spr-issue (accessed May 31, 2024).
    </CitationExampleBox>
    <p>
      <strong>Citing a newspaper article:</strong>
    </p>

    <CitationExampleBox>
      [2] N. Perpitch, &quot;Green groups battle to overturn gas plan,&quot;{' '}
      <em>The Australian,</em> p. 2, Sept. 7, 2010.
    </CitationExampleBox>
  </GuideSection>
);

const CiteConferencePapers: React.FC = () => (
  <GuideSection id="ConferencePapers">
    <GuideHeader>Cite Conference Papers in IEEE</GuideHeader>
    <p>
      Conference papers are a gold mine of up-to-the-minute research findings,
      often uncovering the latest breakthroughs before they reach journals. Be
      it an international symposium on AI or a regional gathering on renewable
      energy, these papers offer a dynamic snapshot of ongoing scholarly
      dialogue.
    </p>
    <p>
      As with all sources, proper citation is essential. This guide will walk
      you through the process of citing conference papers in the IEEE style.
      Below you will see the structure for a conference paper citation entry in
      the IEEE reference list:
    </p>
    <CitationBlock header="Citing a conference paper in IEEE">
      [citation number] Author(s) Initial(s). Last Name, &quot;Title of
      paper,&quot; in <em>Abbreviated Name of Conf.</em>, (location of
      conference is optional), (Month and day(s) if provided) year, pp. page
      range.
    </CitationBlock>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      [1] S. P. Bingulac, &quot;On the compatibility of adaptive
      controllers,&quot; in{' '}
      <em>Proc. 4th Annu. Allerton Conf. Circuit Syst. Theory</em>, New York,
      NY, USA, 1994, pp. 8-16.
    </CitationExampleBox>
    <p>
      For conference proceedings found online, include the URL or DOI at the end
      of the citation.
    </p>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      [2] G. Veruggio, &quot;The EURON roboethics roadmap,&quot; in{' '}
      <em>Proc. Humanoids 2006: 6th IEEE-RAS Int. Conf. Humanoid Robots</em>,
      2006, pp. 612-617, doi: 10.1109.
    </CitationExampleBox>
    <p>
      For papers presented at a symposium or workshop within a conference (a
      smaller event within a larger conference), include the title of the
      symposium or workshop.
    </p>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      [3] G. W. Juette and L. E. Zeffanella, &quot;Radio noise currents on short
      sections on bundle conductors,&quot; presented at the IEEE Summer Power
      Meeting, Dallas, TX, USA, Jun. 22-27, 1990.
    </CitationExampleBox>
    <p>
      If no author is listed, begin the citation with the title of the paper.
      Similarly, when page numbers are not available, simply exclude them from
      the citation.
    </p>
  </GuideSection>
);

const CiteSocialMedia: React.FC = () => (
  <GuideSection id="SocialMedia">
    <GuideHeader>Cite Social Media Posts in IEEE</GuideHeader>
    <p>
      In today&apos;s digitally dominated world, social media platforms have
      become the go-to places for breaking news, academic discourse, and
      cultural phenomena. As these platforms are increasingly integrated into
      our research and scholarly conversations, it&apos;s essential to know how
      to properly cite social media sources. This guide will take you through
      the necessary steps to cite social media posts in the IEEE citation style.
    </p>
    <p>
      Although IEEE style does not provide a standard format for citing social
      media, an adaption of the general IEEE citation style can be used for
      social media posts. This format includes the most important elements of
      the source information and helps ensure your citations are useful to your
      readers.
    </p>
    <p>
      Below you will find a general structure for a social media post citation
      entry in the IEEE reference list:
    </p>
    <CitationBlock header="Citing social media posts in IEEE">
      [citation number] Author(s) Initial(s). Last Name, Full Tweet or “Title of
      post,” <em>Platform</em>, Month Day, Year. URL (accessed Month Day, Year).
    </CitationBlock>
    <p>
      Below you will find some citation examples from popular social media
      platforms:
    </p>
    <p>
      <strong>IEEE citation for a Twitter post:</strong>
    </p>
    <CitationExampleBox>
      [1] M. H. Choi [@choitotheworld], &quot;Can someone fix bacon packaging
      this has gone on long enough,&quot;{' '}
      <TextWithColor>
        <em>Twitter</em>
      </TextWithColor>
      , Apr 16, 2020.
      https://twitter.com/choitotheworld/status/1250444150215827458 (accessed
      Aug. 5, 2020).
    </CitationExampleBox>
    <p>
      <strong>IEEE citation for a Facebook post:</strong>
    </p>
    <CitationExampleBox>
      [2] NASA [@nasa], &quot;Scientists have discovered water molecules inside
      Clavius Crater,&quot;{' '}
      <TextWithColor>
        <em>Facebook</em>
      </TextWithColor>
      , Oct 26, 2020.
      https://www.facebook.com/NASA/photos/a.67899501771/10158494248796772
      (accessed May 5, 2023).
    </CitationExampleBox>
    <p>
      <strong>IEEE citation for an Instagram post:</strong>
    </p>
    <CitationExampleBox>
      [3] D. D. Johnson [@therock], &quot;Newest official poster for our movie
      event of the summer: JUNGLE CRUISE...,&quot;{' '}
      <TextWithColor>
        <em>Instagram</em>
      </TextWithColor>
      , May 27, 2021. https://www.instagram.com/p/CPW0qDhlKRa (accessed Jun. 6,
      2015).
    </CitationExampleBox>
    <p>
      These guidelines should provide a useful starting point for citing social
      media posts in the IEEE style. It&apos;s essential to verify the specific
      citation guidelines of your institute or the publication you&apos;re
      submitting to, as they may have specific preferences or requirements.
    </p>
  </GuideSection>
);

const CiteImagesArtworks: React.FC = () => (
  <GuideSection id="ImagesArtworks">
    <GuideHeader>Cite Images & Figures in IEEE</GuideHeader>
    <p>
      As they say, a picture is worth a thousand words. Images and figures can
      profoundly enhance our understanding and interpretation of a subject, so
      when incorporating this medium into your scholarly work, it is crucial to
      properly cite them to give credit to their creators.
    </p>
    <p>
      The IEEE citation style does not provide explicit guidelines for citing
      images and figures. However, you can adapt the general IEEE citation
      format to suit these materials. It&apos;s important to give credit to the
      creators and provide enough information for your readers to locate the
      original source.
    </p>
    <p>
      This guide will take you through the specifics of citing images and
      artworks in the IEEE style. Below you will find the IEEE citation
      structure for an image or an artwork:
    </p>
    <CitationBlock header="Citing images and figures in IEEE">
      Fig. #. Caption of figure. (x) Caption of part (if applicable). (x)
      Caption of part (if applicable). [citation number]
    </CitationBlock>
    <p>
      <strong>IEEE citation for a figure:</strong>
    </p>
    <CitationExampleBox>
      Fig. 4. Intercomplex crosstalk characteristics. (a) Electrode
      transmission. (b) Interelectrode crosstalk. [1]
    </CitationExampleBox>
    <p>
      Always place the Figure title/caption below the figure. The Figure, and
      Figure title/captions must be centre-justified.
    </p>
    <p>
      When referring to multiple parts of figures: Use the singular Fig., not
      Figs. (e.g. Fig. 4(a) and 4(b)).
    </p>
    <p>
      If you adapt, amend, or otherwise modify the image, indicate this at the
      end of your caption.
    </p>
    <p>
      Below we have included several citation examples for popular image
      mediums:
    </p>
    <p>
      <strong>IEEE citation for a table:</strong>
    </p>
    <CitationExampleBox>
      <p> TABLE IX</p>
      Optimal Wavelength as a Function of Polarizer Angle. (a) Wavelength for
      External Cavity. (b) Estimated Wavelength for Laser Diode [2]
    </CitationExampleBox>
    <p>
      <strong>IEEE citation for an image:</strong>
    </p>
    <CitationExampleBox>
      Fig. 8. 5G Wireless tower. Adapted from [2]
    </CitationExampleBox>
    <p>
      Remember that the goal of citations is to provide enough information for
      the reader to find your source. Always provide as much information as
      possible and be sure to follow any specific guidelines given by your
      instructor or the publisher.
    </p>
  </GuideSection>
);

const CiteThesesDissertations: React.FC = () => (
  <GuideSection id="ThesesDissertations">
    <GuideHeader>Cite Theses & Dissertations in IEEE</GuideHeader>
    <p>
      Theses and dissertations serve as critical resources, often containing
      detailed and unique insights into specific research topics. When these
      works inform your own research, it is essential to cite them correctly,
      not just to acknowledge the original authors, but also to enable readers
      to locate these sources.
    </p>
    <p>
      This guide will help you understand the specifics of citing theses and
      dissertations in the IEEE citation style. The IEEE format for citing a
      thesis or dissertation is similar to the one for books but with some minor
      differences:
    </p>
    <CitationBlock header="Citing a thesis or dissertation in IEEE">
      [citation number] Author(s) Initial(s). Last Name, “Title of thesis,” M.S.
      thesis or Ph.D. dissertation, Abbrev. Dept., Abbrev. Univ., City of Univ.,
      Abbrev. State, Year.
    </CitationBlock>
    <p>
      Now, let&apos;s explore some examples for citing theses and dissertations
      in IEEE:
    </p>
    <p>
      <strong>Citation for a master&apos;s thesis:</strong>
    </p>
    <CitationExampleBox>
      [1] N. Kawasaki, “Parametric study of thermal and chemical nonequilibrium
      nozzle flow,” M.S. thesis, Dept. Electron. Eng., Osaka Univ., Osaka,
      Japan, 1993.
    </CitationExampleBox>
    <p>
      <strong>Citation for a doctoral dissertation:</strong>
    </p>
    <CitationExampleBox>
      [2] J. O. Williams, “Narrow-band analyzer,” Ph.D. dissertation, Dept.
      Elect. Eng., Harvard Univ., Cambridge, MA, USA, 1993.
    </CitationExampleBox>
    <p>
      <strong>Citation for a dissertation accessed online:</strong>
    </p>
    <CitationExampleBox>
      [3] F. Jensen, “Electromagnetic near-field far-field correlations,” Ph.D.
      dissertation, Dept. Elect. Eng., Tech. Univ. Denmark, Lyngby, Denmark,
      1970. [Online]. Available: www.tud.ed/jensen/diss
    </CitationExampleBox>
    <p>
      These examples should help you understand how to cite theses and
      dissertations in IEEE format. As always, consult with your instructor or
      publisher to ensure you meet any specific citation requirements they may
      have.
    </p>
  </GuideSection>
);

const CiteDataSetsDatabases: React.FC = () => (
  <GuideSection id="DataSetsDatabases">
    <GuideHeader>Cite Data Sets & Databases in IEEE</GuideHeader>
    <p>
      In the era of big data and digital technology, data sets and databases are
      becoming increasingly important as primary research sources. These unique
      sources provide vast amounts of information that can be analyzed to
      uncover new insights. This guide will explore the specifics of citing data
      sets and databases in the IEEE citation style. Below you will find the
      structure for citing data sets or databases in IEEE:
    </p>
    <CitationBlock header="Citing data sets and databases in IEEE">
      [citation number] Author(s) Initial(s). Last Name, Author, &quot;Dataset
      Title.&quot; (Date, Year). Distributed by Publisher/Distributor. URL or
      DOI.
    </CitationBlock>
    <p>
      Let&apos;s take a look at some examples on how to cite data sets and
      databases in IEEE:
    </p>
    <p>
      <strong>Citation for a data set:</strong>
    </p>
    <CitationExampleBox>
      [1] S. Ansolabehere, M. Palmer, and A. Lee. “Precinct-level election data.
      V1.” January 20, 2014. Distributed by Harvard Election Data Archive.
      https://hdl.handle.net/1902.1/21919
    </CitationExampleBox>

    <p>
      <strong>Citation for a datasheet:</strong>
    </p>
    <CitationExampleBox>
      [3] Texas Instruments, &quot;High speed CMOS logic analog
      multiplexers/demultiplexers,&quot; 74HC4051 datasheet, Nov. 1997 [Revised
      Sept. 2002].
    </CitationExampleBox>
    <p>
      These examples should provide a clear idea of how to cite data sets and
      databases in IEEE style. Remember to always check with your instructor or
      the publishing authority for specific citation requirements.
    </p>
  </GuideSection>
);

const CiteMobileApplications: React.FC = () => (
  <GuideSection id="MobileApplications">
    <GuideHeader>Cite Mobile Applications in IEEE</GuideHeader>
    <p>
      In the digital age, mobile applications are an essential part of our daily
      life, providing various services and resources right at our fingertips.
      Occasionally, they even serve as relevant sources of information in
      academic research. It&apos;s important to know how to cite them correctly
      when they contribute to your work. In this guide, we will discuss how to
      cite mobile applications using the IEEE citation style.
    </p>
    <p>
      The IEEE citation style does not provide an explicit format for citing
      mobile applications. However, you can still adapt the general format to
      suit this medium. It&apos;s crucial to mention the app&apos;s title, the
      version (if available), the site or organization that made it available,
      and the URL or the site where it was accessed.
    </p>
    <p>Here&apos;s a general format for citing mobile apps in IEEE:</p>
    <CitationBlock header="Citing mobile applications in IEEE">
      [citation number] Title of App, Company that made the App, version (if
      available). [Mobile app]. Available: URL
    </CitationBlock>
    <p>
      Let&apos;s take a look at some examples on how to cite mobile applications
      in IEEE:
    </p>
    <p>
      <strong>Citation for an app available on the App Store:</strong>
    </p>
    <CitationExampleBox>
      [1] Pokemon GO, Niantic, Inc. [Mobile app]. Available:
      https://apps.apple.com/app/pokemon-go/id1094591345
    </CitationExampleBox>
    <p>
      <strong>Citation for an app available on Google Play:</strong>
    </p>
    <CitationExampleBox>
      [2] Among Us, Innersloth LLC. [Mobile app]. Available:
      https://play.google.com/store/apps/details?id=com.innersloth.spacemafia
    </CitationExampleBox>
    <p>
      <strong>Citation for an app with a specific version:</strong>
    </p>
    <CitationExampleBox>
      [3] Minecraft, Mojang, v1.18. [Mobile app]. Available:
      https://play.google.com/store/apps/details?id=com.mojang.minecraftpe
    </CitationExampleBox>
    <p>
      This guide should assist you in citing mobile applications in the IEEE
      style. Always follow specific instructions provided by your instructor or
      the publishing authority.
    </p>
  </GuideSection>
);

const CiteTechnicalReports: React.FC = () => (
  <GuideSection id="TechnicalReports">
    <GuideHeader>Cite Technical Reports in IEEE</GuideHeader>
    <p>
      Technical reports are a key resource in fields such as engineering and
      computer science. These documents are rich with detailed insights and
      cutting-edge research findings, often produced by corporations, government
      agencies, or research institutions. This guide will help you master the
      art of citing technical reports in the IEEE citation style.
    </p>
    <p>Here&apos;s a general format for citing technical reports in IEEE:</p>
    <CitationBlock header="Citing technical reports in IEEE">
      [citation number] Author(s) Initial(s). Last Name, &quot;Title of
      report,&quot; Abbrev. Name of Co., City of Co., Abbrev. State, Country,
      Rep. Number, Year.
    </CitationBlock>
    <p>
      Now let&apos;s dive into some examples on how to cite technical reports in
      IEEE:
    </p>
    <p>
      <strong>Citation for a technical report available in print:</strong>
    </p>
    <CitationExampleBox>
      [1] E. E. Reber, R. L. Michell, and C. J. Carter, “Oxygen absorption in
      the earth&apos;s atmosphere,” Aerospace Corp., Los Angeles, CA, USA,Tech.
      Rep. TR-0200 (4230-46)-3, Nov. 1988.
    </CitationExampleBox>

    <p>
      <strong>Citation for a technical report available online:</strong>
    </p>
    <CitationExampleBox>
      [2] Bureau of Meteorology, “Bureau of Meteorology: Measuring Rainfall in
      Australia,” 2005. Available:
      https://bom.gov.au/climate/cdo/about/definitionsrain.shtml#meanrainfall
      (accessed Dec. 3, 2007).
    </CitationExampleBox>
    <p>
      If the technical report was accessed online, you should include the URL or
      DOI at the end of the citation, along with the (accessed date)
      information.
    </p>
    <p>
      Some reports may not have a report number. In this case, you can simply
      omit this part from the citation.
    </p>
  </GuideSection>
);

const CitePatents: React.FC = () => (
  <GuideSection id="Patents">
    <GuideHeader>Cite Patents in IEEE</GuideHeader>
    <p>
      Patents are an important resource for researchers as they provide
      comprehensive details about new inventions and technical processes, often
      holding crucial information not found elsewhere. This guide will provide
      insights into how to cite patents correctly in the IEEE citation style.
    </p>
    <p>Here&apos;s the general format for citing patents in IEEE:</p>
    <CitationBlock header="Citing patents in IEEE">
      [citation number] Inventor(s), “Title of patent,” Country Patent number,
      Month, Day, Year.
    </CitationBlock>
    <p>
      Below we have included several examples for popular types of patent
      citations:
    </p>
    <p>
      <strong>Citation for a U.S. patent:</strong>
    </p>
    <CitationExampleBox>
      [1] A. Stark, &quot;Flying Powered Armor Suit,&quot;{' '}
      <TextWithColor>U.S. Patent 8 277 236</TextWithColor>, September 30, 2001.
    </CitationExampleBox>
    <p>
      <strong>
        Citation for a patent from another country (in this case, Japan):
      </strong>
    </p>
    <CitationExampleBox>
      [2] A. T. Pikachu, &quot;Electric Power Generation and Storage,&quot;{' '}
      <TextWithColor>Japan Patent JP 4822-8832</TextWithColor>, November 17,
      2022.
    </CitationExampleBox>
    <p>
      <strong>Citation for a patent with multiple inventors:</strong>
    </p>
    <CitationExampleBox>
      [3] <TextWithColor>B. Banner and T. Stark</TextWithColor>, &quot;Gamma
      Radiation Shield,&quot; U.S. Patent 9 765 432, March 12, 2024.
    </CitationExampleBox>
    <p>
      These examples should guide you on how to cite patents in the IEEE style.
      As always, consult with your instructor or the publication you&apos;re
      submitting to for any specific requirements they may have.
    </p>
  </GuideSection>
);

const CiteCourseMaterials: React.FC = () => (
  <GuideSection id="CourseMaterials">
    <GuideHeader>Cite Course Materials in IEEE</GuideHeader>
    <p>
      Course materials can range from lecture slides to course handbooks, lab
      manuals, and online modules. Knowing how to correctly cite these materials
      in your work is crucial. This guide will walk you through citing course
      materials in the IEEE style.
    </p>
    <p>Here&apos;s the general format for citing course materials in IEEE:</p>
    <CitationBlock header="Citing course materials in IEEE">
      [citation number] Author(s) Initial(s). Last Name, &quot;Title of
      Material.&quot; (Semester). Title of Course. University/Publisher
      Location: University/Publisher Name.
    </CitationBlock>
    <p>
      Now let&apos;s dive into some examples on how to cite course materials in
      IEEE:
    </p>
    <p>
      <strong>Citation for lecture notes:</strong>
    </p>
    <CitationExampleBox>
      [1] R. Hagrid, &quot;Magical Creatures{' '}
      <TextWithColor>Lecture Notes.</TextWithColor>&quot; (2023, Fall). Care of
      Magical Creatures, COMC 101. Hogsmeade, Scotland: Hogwarts School of
      Witchcraft and Wizardry.
    </CitationExampleBox>
    <p>
      <strong>Citation for a course handbook:</strong>
    </p>
    <CitationExampleBox>
      [2] P. Parker, &quot;Handbook.&quot; (2016, Spring). Introduction to
      Photography , PHO 201. Brooklyn, New York: Midtown High School.
    </CitationExampleBox>

    <p>
      For unpublished materials such as personal class notes or unrecorded
      lectures, it is generally recommended to cite these in the text and not in
      the reference list. Always check with your professor or the publication
      you&apos;re submitting to for their specific requirements.
    </p>
    <p>
      The overall goal of citation is to provide enough detail for the reader to
      locate your sources. Always provide as much information as possible and
      adhere to the specific guidelines provided by your instructor or the
      publication.
    </p>
  </GuideSection>
);

const CitePersonalCommunications: React.FC = () => (
  <GuideSection id="PersonalCommunications">
    <GuideHeader>Cite Personal Communications in IEEE</GuideHeader>
    <p>
      In IEEE style, personal communications such as private letters,
      conversations, emails, or interviews are not typically included in the
      reference list because they do not provide recoverable data. They are
      cited in the text with an indication of the communicator and the date of
      communication.
    </p>
    <p>
      Here&apos;s the general structure for citing personal communications in
      your IEEE paper:
    </p>
    <p>
      <strong>In-text citation:</strong>
    </p>
    <CitationBlockMini>
      (Communicator, personal communication, Month Day, Year)
    </CitationBlockMini>

    <p>
      Let&apos;s explore some examples on how to cite personal communications in
      IEEE:
    </p>
    <p>
      <strong>IEEE citation for a personal interview:</strong>
    </p>
    <p>
      The Vice-Chancellor&apos;s statement was confirmed during an interview{' '}
      <IntextCitation>
        (P. Dawkins, Vice-Chancellor, Victoria University, personal
        communication, October 11, 2014)
      </IntextCitation>
      .
    </p>
    <p>
      <strong>IEEE citation for a letter or email:</strong>
    </p>
    <p>
      An email{' '}
      <IntextCitation>
        (N. Rahman, personal communication, September 2, 2014)
      </IntextCitation>{' '}
      confirming that results are accurate ...
    </p>
    <p>
      <strong>IEEE citation for a private conversation:</strong>
    </p>
    <p>
      The Professor&apos;s statement was confirmed during a phone interview{' '}
      <IntextCitation>
        (S. Richards, personal communication, July 21, 2015)
      </IntextCitation>
      .
    </p>

    <p>
      Below is an example of how a personal communication citation would appear
      within a text in the IEEE citation style:
    </p>
    <blockquote>
      In a recent discussion about the developments in the Stark Industries,
      Tony Stark confirmed they were working on a new, more efficient arc
      reactor{' '}
      <IntextCitation>
        (T. Stark, personal communication, May 10, 2023)
      </IntextCitation>
      . The reactor promises to be a big breakthrough, providing a sustainable
      energy source that could revolutionize the industry.
    </blockquote>
    <p>
      As a reminder, if you&apos;re citing personal communications, always get
      permission from your source, and be sure that this type of source is
      acceptable for your research or assignment.
    </p>
  </GuideSection>
);

const FinalNotes: React.FC = () => (
  <GuideSection id="FinalNotes">
    <GuideHeader>IEEE Style: Final Notes</GuideHeader>
    <p>
      Navigating the IEEE citation style, we&apos;ve seen its comprehensive
      approach to different sources, from books and journal articles to websites
      and personal communications. Its numeric system allows for clear and
      concise in-text citations, perfect for the engineering and technology
      fields where it&apos;s most commonly used.
    </p>
    <p>
      We hope this guide will serve as your roadmap to understanding and
      implementing the IEEE citation style effectively, enhancing your scholarly
      writing. Meanwhile, <Link href="/">Bibcitation</Link> is always here to
      help you with all your citation needs, offering accurate and easy-to-use
      citation tools.
    </p>
  </GuideSection>
);

export default IEEEGuide;
